import posthog from 'posthog-js';
import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import { useStore } from '../../store';
import { isUserLoggedIn } from '../../utils/analytics';
import { ProductType } from '../../types/common';
import { getModelsMeasurements, parseMetafields } from '../../utils/product';
import { Disclaimer } from '../Typography';

interface MiniCarouselProps {
  children: React.ReactNode[];
  product?: Shopify.Product;
  initialIndex?: number;
  autoPlayInterval?: number; // milliseconds; omit or 0 to disable
  showDots?: boolean;
  showArrows?: boolean;
  onChange?: (index: number) => void;
  transitionSpeed?: string; // CSS transition speed, e.g. '500' for 500ms
  isComingSoon?: boolean;
  isPetition?: boolean;
  isPreview?: boolean;
}

const Container = styled.div<{ ready: boolean }>`
  position: relative;
  width: 100%;
  overflow: hidden;
  /* Hide until ready, to avoid flicker */
  visibility: ${({ ready }) => (ready ? 'visible' : 'hidden')};
`;

const Slider = styled.div<{
  translateX: number;
  isDragging: boolean;
  transitionSpeed: string;
  noTransition?: boolean;
}>`
  display: flex;
  transform: translateX(${({ translateX }) => translateX}px);
  transition: ${({ isDragging, noTransition, transitionSpeed }) =>
    isDragging || noTransition ? 'none' : `transform ${transitionSpeed}ms ease`};
  touch-action: pan-y;
  position: relative;
`;

const Slide = styled.div`
  flex-shrink: 0;
  width: 100%;
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 8px;
`;

const Dot = styled.button<{ active: boolean }>`
  border: none;
  background: ${({ theme, active }) => (active ? theme.colors.carouselDotsActive : theme.colors.carouselDotsInactive)};
  border-radius: 50%;
  width: 8px;
  height: 8px;
  margin: 0 4px;
  padding: 0;
  cursor: pointer;
`;

const DisclaimerWrapper = styled.div`
  display: flex;
  margin: 16px 0;
`;

const CarouselArrowWrapper = styled.div<{ direction: 'left' | 'right'; isTransitioning?: boolean }>`
  cursor: pointer;
  position: absolute;
  top: 50%;
  height: 34px;
  width: 34px;
  ${({ direction }) => (direction === 'left' ? 'left: 8px;' : 'right: 8px;')}
  z-index: 1;
  transform: translateY(-90%);
  opacity: ${({ isTransitioning }) => (isTransitioning ? 0.5 : 1)};
  pointer-events: ${({ isTransitioning }) => (isTransitioning ? 'none' : 'auto')};
  cursor: ${({ isTransitioning }) => (isTransitioning ? 'default' : 'pointer')};
`;

const MiniCarousel: React.FC<MiniCarouselProps> = ({
  children,
  product,
  initialIndex = 0,
  autoPlayInterval,
  showDots = true,
  showArrows = true,
  onChange,
  transitionSpeed = '500',
  isComingSoon = false,
  isPetition = false,
  isPreview = false,
}: MiniCarouselProps) => {
  const { state } = useStore();
  const containerRef = useRef<HTMLDivElement>(null);

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [prevTranslate, setPrevTranslate] = useState(0);
  const [noTransition, setNoTransition] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const autoplayRef = useRef<number | null>(null);
  const productType = product?.productType;
  const metafields = parseMetafields(product?.metafields);

  const totalSlides = children.length;
  // Clone last & first slides for infinite-loop illusion
  const extendedSlides = [
    children[totalSlides - 1], // Clone of last slide
    ...children, // Originals
    children[0], // Clone of first slide
  ];

  // Setup autoplay
  const startAutoplay = useCallback(() => {
    if (autoPlayInterval && totalSlides > 1 && isInitialized) {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
      autoplayRef.current = window.setInterval(() => {
        setCurrentIndex((prev) => prev + 1);
      }, autoPlayInterval);
    }
  }, [autoPlayInterval, totalSlides, isInitialized]);

  useEffect(() => {
    startAutoplay();
    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
        autoplayRef.current = null;
      }
    };
  }, [startAutoplay]);

  // Handle wrapping / onChange
  useEffect(() => {
    if (!isInitialized) return;
    const realIndex = currentIndex + 1; // Because of the prepended clone
    const translate = -realIndex * containerWidth;
    const transitionSpeedMs = parseInt(transitionSpeed, 10);

    setCurrentTranslate(translate);
    setPrevTranslate(translate);

    // If we've gone “past” the real slides, snap back after the CSS transition
    if (currentIndex >= totalSlides) {
      // At cloned-first ⇒ jump to real first
      setTimeout(() => {
        setNoTransition(true);
        setCurrentIndex(0);
        const newTranslate = -1 * containerWidth;
        setCurrentTranslate(newTranslate);
        setPrevTranslate(newTranslate);
        // Re‐enable transitions after a small delay
        setTimeout(() => setNoTransition(false), 50);
      }, transitionSpeedMs);
    } else if (currentIndex < 0) {
      // At cloned-last ⇒ jump to real last
      setTimeout(() => {
        setNoTransition(true);
        setCurrentIndex(totalSlides - 1);
        const newTranslate = -totalSlides * containerWidth;
        setCurrentTranslate(newTranslate);
        setPrevTranslate(newTranslate);
        setTimeout(() => setNoTransition(false), 50);
      }, transitionSpeedMs);
    }

    // Fire onChange with the actual 0-based index
    const actualIndex = ((currentIndex % totalSlides) + totalSlides) % totalSlides;
    onChange?.(actualIndex);
  }, [currentIndex, containerWidth, totalSlides, onChange, isInitialized, transitionSpeed]);

  // INITIAL MEASUREMENT & POSITION
  // We want to measure and “jump” the slider into place BEFORE the browser paints,
  // so that there is never a moment when translateX is 0.
  useLayoutEffect(() => {
    if (children.length <= 1) {
      // No need to initialize for a single slide
      setIsInitialized(true);
      setNoTransition(false);
      return;
    }

    const updateWidthAndPosition = () => {
      if (!containerRef.current) return;
      const w = containerRef.current.offsetWidth;
      setContainerWidth(w);

      // Compute where the slider must start: (initialIndex + 1) because of the prepended clone
      const initialTranslate = -(initialIndex + 1) * w;
      setCurrentTranslate(initialTranslate);
      setPrevTranslate(initialTranslate);

      // We’re now “ready to show” without any transition
      setNoTransition(true);
      setIsInitialized(true);

      // After a brief tick, enable transitions for future moves
      setTimeout(() => {
        setNoTransition(false);
      }, 0);
    };

    // Do it once on mount
    updateWidthAndPosition();

    // Also update on window.resize
    window.addEventListener('resize', updateWidthAndPosition);
    return () => {
      window.removeEventListener('resize', updateWidthAndPosition);
    };
  }, [initialIndex, children.length]);

  // POINTER / DRAG HANDLERS
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (totalSlides <= 1) return;

      // Prevent carousel slide change when using 360 viewer
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT') {
        e.stopPropagation();
        return;
      }

      // Stop autoplay while dragging
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
        autoplayRef.current = null;
      }

      e.preventDefault();
      setIsDragging(true);
      setStartX(e.clientX);
      setHasMoved(false);
    },
    [totalSlides],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return;
      const diff = e.clientX - startX;
      if (Math.abs(diff) > 5) {
        setHasMoved(true);
      }
      setCurrentTranslate(prevTranslate + diff);
    },
    [isDragging, startX, prevTranslate],
  );

  const handleDragComplete = () =>
    posthog.capture('mastheadCarousel_slide_changed', {
      isUserLoggedIn: isUserLoggedIn(state.user),
      selected_slide_index: currentIndex,
      total_slide_count: totalSlides,
    });

  const finishDrag = useCallback(() => {
    setIsDragging(false);
    const moved = currentTranslate - prevTranslate;
    if (moved < -containerWidth / 3) {
      setCurrentIndex(currentIndex + 1);
    } else if (moved > containerWidth / 3) {
      setCurrentIndex(currentIndex - 1);
    } else {
      setCurrentTranslate(prevTranslate);
    }
    handleDragComplete();
  }, [currentTranslate, prevTranslate, containerWidth, currentIndex, totalSlides]);

  const handlePointerUp = useCallback(() => {
    if (hasMoved) finishDrag();
    else setIsDragging(false);
  }, [finishDrag, hasMoved]);

  const handlePointerLeave = useCallback(() => {
    if (isDragging) finishDrag();
  }, [isDragging, finishDrag]);

  const handleDotClick = useCallback(
    (index: number) => {
      posthog.capture('mastheadCarousel_dot_click', {
        index,
        isUserLoggedIn: isUserLoggedIn(state.user),
      });
      setCurrentIndex(index);
    },
    [state.user],
  );

  // Prevents glitchy wrapAround when spamming arrows
  useEffect(() => {
    if (!isDragging && !noTransition) {
      const timer = setTimeout(() => setIsTransitioning(false), Number(transitionSpeed));
      return () => clearTimeout(timer);
    }
  }, [transitionSpeed, isTransitioning]);

  const handleArrowClick = useCallback(
    (direction: 'left' | 'right') => {
      if (isTransitioning) return;

      setIsTransitioning(true);

      posthog.capture('mastheadCarousel_arrow_click', {
        direction,
        isUserLoggedIn: isUserLoggedIn(state.user),
      });

      setCurrentIndex((prevIndex) => prevIndex + (direction === 'left' ? -1 : 1));
    },
    [isTransitioning],
  );

  // Actual current index for dot rendering
  const actualCurrentIndex = ((currentIndex % totalSlides) + totalSlides) % totalSlides;

  return (
    <Container
      ref={containerRef}
      ready={isInitialized}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      onClickCapture={(e) => {
        if (hasMoved) {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
    >
      {showArrows && children.length > 1 && (
        <CarouselArrowWrapper
          onClick={() => handleArrowClick('left')}
          direction="left"
          isTransitioning={isTransitioning}
        >
          <Image src="/assets/icons/arrow_left.svg" alt="Previous" width={34} height={34} priority />
        </CarouselArrowWrapper>
      )}
      <Slider
        translateX={currentTranslate}
        isDragging={isDragging}
        noTransition={noTransition}
        transitionSpeed={transitionSpeed}
      >
        {extendedSlides.map((child, idx) => (
          // We only use idx as key here because slides themselves won’t change
          // eslint-disable-next-line react/no-array-index-key
          <Slide key={idx}>{child}</Slide>
        ))}
      </Slider>
      {showArrows && children.length > 1 && (
        <CarouselArrowWrapper
          onClick={() => handleArrowClick('right')}
          direction="right"
          isTransitioning={isTransitioning}
        >
          <Image src="/assets/icons/arrow_right.svg" alt="Next" width={34} height={34} priority />
        </CarouselArrowWrapper>
      )}
      {productType === ProductType.VinylFigure && (!isComingSoon || isPreview) && (
        <DisclaimerWrapper>
          <Disclaimer data-testid="disclaimer">3D digital render shown</Disclaimer>
        </DisclaimerWrapper>
      )}
      {isPetition && (
        <DisclaimerWrapper>
          <Disclaimer>Reference images provided by creator. Final product results may vary.</Disclaimer>
        </DisclaimerWrapper>
      )}
      {product && !!getModelsMeasurements(product.id) && (
        <DisclaimerWrapper>
          <Disclaimer>{getModelsMeasurements(product.id)}</Disclaimer>
        </DisclaimerWrapper>
      )}
      {product && !!metafields.disclaimer && !isComingSoon && (
        <DisclaimerWrapper>
          <Disclaimer>{metafields.disclaimer}</Disclaimer>
        </DisclaimerWrapper>
      )}
      {showDots && children.length > 1 && (
        <Dots>
          {children.map((_, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <Dot key={idx} active={idx === actualCurrentIndex} onClick={() => handleDotClick(idx)} />
          ))}
        </Dots>
      )}
    </Container>
  );
};

export default MiniCarousel;
